import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import RandomPopup from './components/RandomPopup';
import SEO from './components/SEO';
import SchemaMarkup from './components/SchemaMarkup';
import Analytics from './components/Analytics';
import { getPopupSettings } from './services/apiService';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Admissions from './pages/Admissions';
import Courses from './pages/Courses';
import PartneredUniversities from './pages/PartneredUniversities';
import VisaSuccessStories from './pages/VisaSuccessStories';
import IELTSSuccessStories from './pages/IELTSSuccessStories';
import Gallery from './pages/Gallery';
import SuccessStories from './pages/SuccessStories';
import Reviews from './pages/Reviews';
import Contact from './pages/Contact';
import BookIELTSMockTest from './pages/BookIELTSMockTest';
import BookFreeCounselling from './pages/BookFreeCounselling';
import BookFreeDemoClass from './pages/BookFreeDemoClass';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Sitemap from './pages/Sitemap';
import Services from './pages/Services';

// Admin
import AdminLogin from './admin/AdminLogin';
import AdminDashboard from './admin/AdminDashboard';

function App() {
  const [showPopup, setShowPopup] = useState(false);
  const [popupSettings, setPopupSettings] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    // Load popup settings
    const loadSettings = async () => {
      try {
        const settings = await getPopupSettings();
        setPopupSettings(settings);
      } catch (error) {
        console.error('Error loading popup settings:', error);
        // Default settings
        setPopupSettings({
          min_interval: 30000,
          max_interval: 90000,
          enabled: true,
          enabled_templates: ['template1'],
        });
      }
    };
    loadSettings();
  }, []);

  // Schedule popup when settings are loaded or when popup closes
  // Schedule popup when settings are loaded or when popup closes
  useEffect(() => {
    if (!popupSettings) return;
    
    // Check if popup is enabled
    if (popupSettings.enabled === false) {
      setShowPopup(false);
      return;
    }

    // Don't schedule if popup is already showing
    if (showPopup) return;

    // Random popup interval based on settings
    const min = popupSettings.min_interval || 30000;
    const max = popupSettings.max_interval || 90000;
    const randomTime = Math.floor(Math.random() * (max - min) + min);
    
    // Select random template if multiple enabled
    const enabled = popupSettings.enabled_templates || ['template1'];
    if (enabled.length > 0) {
      const randomTemplate = enabled[Math.floor(Math.random() * enabled.length)];
      setSelectedTemplate(randomTemplate);
    }
    
    const timer = setTimeout(() => {
      // Double-check settings are still valid before showing
      if (popupSettings && popupSettings.enabled !== false) {
        setShowPopup(true);
      }
    }, randomTime);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [popupSettings, showPopup]);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <div className="min-h-screen flex flex-col">
        <SEO />
        <SchemaMarkup />
        <Analytics />
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/*" element={<AdminDashboard />} />
          <Route
            path="/*"
            element={
              <>
                <Header />
                <main className="flex-grow">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/admissions" element={<Admissions />} />
                    <Route path="/courses" element={<Courses />} />
                    <Route path="/partnered-universities" element={<PartneredUniversities />} />
                    <Route path="/visa-success-stories" element={<VisaSuccessStories />} />
                    <Route path="/ielts-success-stories" element={<IELTSSuccessStories />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/success-stories" element={<SuccessStories />} />
                    <Route path="/reviews" element={<Reviews />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/book-ielts-mock-test" element={<BookIELTSMockTest />} />
                    <Route path="/book-free-counselling" element={<BookFreeCounselling />} />
                    <Route path="/book-free-demo-class" element={<BookFreeDemoClass />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/sitemap" element={<Sitemap />} />
                    <Route path="*" element={<div className="max-w-7xl mx-auto px-6 py-32"><h1>Page not found</h1><p>The page you requested could not be found.</p><a href="/">Return to home</a></div>} />
                  </Routes>
                </main>
                <Footer />
                {showPopup && popupSettings && popupSettings.enabled !== false && (
                  <RandomPopup 
                    onClose={() => {
                      setShowPopup(false);
                    }} 
                    templateId={selectedTemplate} 
                  />
                )}
              </>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;

